import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { usePeriodeStore } from "@/hooks/use-periode";
import { apiClient } from "@/lib/api-client";
import { Calendar, Clock, Loader2, MessageSquare, Send } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

interface Reply {
  pesan: string;
  role: string;
  nama: string;
  alias?: string;
  created_at: string;
}

interface Pengaduan {
  id: string;
  judul: string;
  isi: string;
  images: string[];
  status: string;
  niu: string;
  nama: string;
  replies: Reply[];
  created_at: string;
  updated_at: string;
}

export const RiwayatPertanyaanDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const [data, setData] = useState<Pengaduan[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [replyTexts, setReplyTexts] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState<Record<string, boolean>>({});

  const { getYearAndSemester } = usePeriodeStore();
  const { year, semester } = getYearAndSemester();

  useEffect(() => {
    if (open) {
      fetchRiwayat();
    }
  }, [open, year, semester]);

  const fetchRiwayat = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const resData = await apiClient<any>(
        `${import.meta.env.VITE_API_BASE_URL}/api/v1/pengaduan?year=${year}&semester=${semester}`,
      );
      setData(resData.datas || []);
    } catch (err: any) {
      setError(err.message || "Terjadi kesalahan");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReply = async (id: string) => {
    const text = replyTexts[id];
    if (!text?.trim()) return;

    setIsSubmitting((prev) => ({ ...prev, [id]: true }));
    try {
      await apiClient(
        `${import.meta.env.VITE_API_BASE_URL}/api/v1/pengaduan/${id}/reply?year=${year}&semester=${semester}`,
        {
          method: "POST",
          body: JSON.stringify({ pesan: text }),
        },
      );

      await fetchRiwayat();

      setReplyTexts((prev) => ({ ...prev, [id]: "" }));
      toast.success("Balasan berhasil dikirim.");
    } catch (err: any) {
      toast.error(err.message || "Gagal mengirim balasan.");
    } finally {
      setIsSubmitting((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
    id: string,
  ) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (replyTexts[id]?.trim() && !isSubmitting[id]) {
        handleReply(id);
      }
    }
  };

  const formatDate = (dateStr: string) => {
    return (
      new Date(dateStr).toLocaleString("id-ID", {
        dateStyle: "long",
        timeStyle: "short",
      }) + " WIB"
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col p-0 overflow-hidden gap-0 bg-slate-100">
        <DialogHeader className="px-6 py-4 border-b border-slate-200 bg-white shrink-0">
          <DialogTitle>Riwayat Pertanyaan & Masukan</DialogTitle>
          <DialogDescription>
            Daftar pertanyaan dan masukan yang pernah Anda ajukan beserta
            jawabannya.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 py-4 custom-scrollbar">
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-500 bg-white rounded-lg border border-red-100">
              {error}
            </div>
          ) : data.length === 0 ? (
            <div className="text-center py-12 text-slate-500 bg-white rounded-lg border border-slate-200">
              Belum ada riwayat pertanyaan untuk periode ini.
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              {data.map((item) => (
                <div
                  key={item.id}
                  className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm"
                >
                  <div className="p-4 border-b border-slate-100">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <h4 className="font-semibold text-slate-800 text-lg leading-tight">
                        {item.judul}
                      </h4>
                      <span
                        className={`text-[10px] px-2.5 py-1 rounded-full font-bold uppercase tracking-wider shrink-0 ${
                          item.status === "closed"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {item.status === "closed" ? "Selesai" : "Terbuka"}
                      </span>
                    </div>
                    <p className="text-slate-500 text-xs flex items-center gap-1.5 mb-3">
                      <Calendar className="w-3.5 h-3.5" />
                      {formatDate(item.created_at)}
                    </p>

                    <div
                      className="text-sm text-slate-700 bg-slate-50/50 p-3 rounded-lg border border-slate-100 mb-3 prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: item.isi }}
                    />

                    {item.images && item.images.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {item.images.map((imgUrl, i) => (
                          <a
                            key={i}
                            href={`${imgUrl}`}
                            target="_blank"
                            rel="noreferrer"
                            className="w-16 h-16 rounded border border-slate-200 overflow-hidden bg-slate-100 hover:opacity-80 transition-opacity"
                          >
                            <img
                              src={`${imgUrl}`}
                              alt="Lampiran"
                              className="w-full h-full object-cover"
                            />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="p-4 bg-slate-50">
                    <h5 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                      <MessageSquare className="w-3.5 h-3.5" /> Balasan
                    </h5>

                    {item.replies && item.replies.length > 0 ? (
                      <div className="flex flex-col gap-3">
                        {item.replies.map((reply, idx) => (
                          <div
                            key={idx}
                            className={`p-3 rounded-lg border text-sm relative ${
                              reply.role === "admin"
                                ? "bg-primary/5 border-primary/10 mr-4"
                                : "bg-white border-slate-200 ml-4"
                            }`}
                          >
                            <div className="flex justify-between items-center mb-1.5 gap-4">
                              <span className="font-semibold text-slate-800 text-xs flex items-center gap-1.5">
                                Tim Remunerasi
                                {reply.alias && (
                                  <span className="bg-primary/10 text-primary px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wide uppercase">
                                    {(() => {
                                      try {
                                        return atob(reply.alias);
                                      } catch (e) {
                                        return reply.alias;
                                      }
                                    })()}
                                  </span>
                                )}
                              </span>
                              <span className="text-[10px] text-slate-400 whitespace-nowrap shrink-0">
                                {formatDate(reply.created_at)}
                              </span>
                            </div>
                            <div className="text-slate-700 whitespace-pre-wrap">
                              {reply.pesan}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-slate-400 italic flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        Belum ada balasan dari Tim Remunerasi Undiksha.
                      </div>
                    )}

                    {(item.status === "open" ||
                      item.status === "in_progress" ||
                      item.status === "waiting_user") && (
                      <div className="mt-4 pt-4 border-t border-slate-200">
                        <div className="flex gap-3">
                          <Textarea
                            placeholder="Tulis balasan Anda di sini (Tekan Enter untuk kirim, Shift+Enter untuk baris baru)..."
                            value={replyTexts[item.id] || ""}
                            onChange={(e) =>
                              setReplyTexts((prev) => ({
                                ...prev,
                                [item.id]: e.target.value,
                              }))
                            }
                            onKeyDown={(e) => handleKeyDown(e, item.id)}
                            className="min-h-20 resize-none text-sm"
                            disabled={isSubmitting[item.id]}
                          />
                          <Button
                            onClick={() => handleReply(item.id)}
                            disabled={
                              !replyTexts[item.id]?.trim() ||
                              isSubmitting[item.id]
                            }
                            className="shrink-0 h-auto"
                          >
                            {isSubmitting[item.id] ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Send className="w-4 h-4 mr-2" />
                                Kirim
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
